import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useValidateInvitationQuery } from "api/invitationApi";
import MarketingPageStyles from "./marketingStyles";
import { MarketingPageRoot } from "./MarketingPageRoot";
import { useMarketingPageEffects } from "./hooks/useMarketingPageEffects";
import SignInModal from "./SignInModal";
import LandingLoader from "./LandingLoader";
import LandingNav from "./LandingNav";
import LandingHero from "./LandingHero";
import LogosMarquee from "./LogosMarquee";
import ProblemSection from "./ProblemSection";
import HowItWorksSection from "./HowItWorksSection";
import FeaturesSection from "./FeaturesSection";
import { CompareSection, StatsSection } from "./CompareSection";
import ReviewsSection from "./ReviewsSection";
import LandingPricingSection from "./LandingPricingSection";
import FAQSection from "./FAQSection";
import { CTASection, LandingFooter } from "./CTASection";

const INVITE_REASON_COPY = {
  expired: "This invitation has expired. You can still create a regular account.",
  used: "This invitation has already been used. You can still create a regular account.",
  revoked: "This invitation is no longer valid. You can still create a regular account.",
  invalid: "This invitation link is invalid. You can still create a regular account.",
};

const MarketingPage = () => {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite") || "";
  const { data: invitation } = useValidateInvitationQuery(inviteToken, {
    skip: !inviteToken,
  });
  const [signInModalOpen, setSignInModalOpen] = useState(false);
  const [isSignUp, setIsSignUp] = useState(true);
  const [loaderDone, setLoaderDone] = useState(false);

  const {
    menuOpen,
    navScrolled,
    openFaqIndex,
    heroVisible,
    heroBgRef,
    pageRef,
    toggleMenu,
    toggleFaq,
    handleMagneticMove,
    handleMagneticLeave,
  } = useMarketingPageEffects({ loaderDone });

  const openSignUp = () => {
    setIsSignUp(true);
    setSignInModalOpen(true);
  };

  const openSignIn = () => {
    setIsSignUp(false);
    setSignInModalOpen(true);
  };

  useEffect(() => {
    if (!inviteToken) return;
    setIsSignUp(true);
    setSignInModalOpen(true);
  }, [inviteToken]);

  return (
    <>
      <MarketingPageStyles />
      <MarketingPageRoot className="marketing-page" ref={pageRef}>
        <LandingLoader done={loaderDone} onComplete={() => setLoaderDone(true)} />
        <LandingNav
          scrolled={navScrolled}
          menuOpen={menuOpen}
          onToggleMenu={toggleMenu}
          onSignIn={openSignIn}
          onSignUp={openSignUp}
        />
        <LandingHero
          heroVisible={heroVisible}
          heroBgRef={heroBgRef}
          onSignUp={openSignUp}
          onMagneticMove={handleMagneticMove}
          onMagneticLeave={handleMagneticLeave}
        />
        <LogosMarquee />
        <ProblemSection />
        <HowItWorksSection />
        <FeaturesSection />
        <CompareSection />
        <StatsSection />
        <ReviewsSection />
        <LandingPricingSection
          onSignUp={openSignUp}
          onMagneticMove={handleMagneticMove}
          onMagneticLeave={handleMagneticLeave}
        />
        <FAQSection openFaqIndex={openFaqIndex} onToggleFaq={toggleFaq} />
        <CTASection onMagneticMove={handleMagneticMove} onMagneticLeave={handleMagneticLeave} />
        <LandingFooter />
      </MarketingPageRoot>

      {signInModalOpen && (
        <SignInModal
          isModalOpen={signInModalOpen}
          toggleModal={() => setSignInModalOpen(false)}
          isSignUpMode={isSignUp}
          onSwitchMode={() => setIsSignUp((prev) => !prev)}
          inviteToken={invitation && !invitation.valid ? "" : inviteToken}
          invitedEmail={invitation?.valid ? invitation.email : ""}
          inviteWarning={
            inviteToken && invitation && !invitation.valid
              ? INVITE_REASON_COPY[invitation.reason] || INVITE_REASON_COPY.invalid
              : ""
          }
        />
      )}
    </>
  );
};

export default MarketingPage;
